import {ActionTree, ActionContext} from 'vuex'
import {ICompaniesStateModel, IRootStateModel} from '../../../interfaces'
import { CompaniesMutations } from './../mutations'
import { ActionTypes } from './types'
import { MutationTypes } from './../mutations/types'
import {
    CompaniesListResponseModel, 
    CompaniesListSortModel, 
    CompaniesListFilterModel,
    DeleteCompaniesListRequestModel,
    ChangeCompanyStatusModel,
    GetCompanyResponseModel,
    CompanyEmployeeModel,
    GetCompanyWithUsersResponseModel,
    CompanyModel
} from './../../../../services/types'
import { BaseClient } from './../../../../services/client/BaseClient'
import { getClientInstance } from './../../../..//services/client/clientProvider'
import { StoreActionTypes } from './../../../types'

type AugmentedActionCompaniesContext = {
    commit<K extends keyof CompaniesMutations>(
      key: K,
      payload: Parameters<CompaniesMutations[K]>[1]
    ): ReturnType<CompaniesMutations[K]>;
} & Omit<ActionContext<ICompaniesStateModel, IRootStateModel>, 'commit'>

export interface CompaniesActions {
    [ActionTypes.SET_COMPANIES_LIST](context: AugmentedActionCompaniesContext): Promise<void>;
    [ActionTypes.SET_COMPANIES_LIST_PAGE](context: AugmentedActionCompaniesContext, page: number): void;
    [ActionTypes.SET_COMPANIES_LIST_PER_PAGE](context: AugmentedActionCompaniesContext, perPage: number): void;
    [ActionTypes.SET_COMPANIES_LIST_SORT](context: AugmentedActionCompaniesContext, sort: CompaniesListSortModel): void;
    [ActionTypes.SET_COMPANIES_LIST_FILTER](context: AugmentedActionCompaniesContext, sort: CompaniesListFilterModel): void;
    [ActionTypes.SET_COMPANIES_COMPANY_CHANGE_STATUS](context: AugmentedActionCompaniesContext, model: ChangeCompanyStatusModel): Promise<void>;
    [ActionTypes.SET_COMPANIES_COMPANY_DELETE](context: AugmentedActionCompaniesContext, model: DeleteCompaniesListRequestModel): Promise<void>;
    [ActionTypes.SET_COMPANIES_COMPANY_CHANGE_NAME](context: AugmentedActionCompaniesContext, name: string): Promise<void>;
    [ActionTypes.SET_COMPANIES_COMPANY_CREATE](context: AugmentedActionCompaniesContext, name: string): Promise<void>;
    [ActionTypes.SET_COMPANIES_SELECTED_COMPANY](context: AugmentedActionCompaniesContext, id: number | null): Promise<void>;
    [ActionTypes.SET_COMPANIES_SELECTED_COMPANY_WITH_USERS_LIST](context: AugmentedActionCompaniesContext, id: number | null): Promise<void>;
    [ActionTypes.SET_COMPANIES_SELECTED_COMPANY_CLEAR](context: AugmentedActionCompaniesContext| null): void;
}

export const actions: ActionTree<ICompaniesStateModel, IRootStateModel> & CompaniesActions = {
    async [ActionTypes.SET_COMPANIES_LIST]({commit, getters}) {

        const client: BaseClient = getClientInstance()

        commit(MutationTypes.SET_COMPANIES_DATA_IS_LOADING, true)

        try {
            const response: CompaniesListResponseModel = await client.getCompaniesList({
                page: getters.getCompaniesListPage as number,
                perPage: getters.getCompaniesListPerPage as number,
                filter: getters.getCompaniesFilterValue,
                sort: getters.getCompaniesSortValue
            }) 

            commit(MutationTypes.SET_COMPANIES_LIST, response.items)
            commit(MutationTypes.SET_COMPANIES_TOTAL, response.total)  
        }
        catch(error) {
            console.log('getCompaniesList', error)
        }
        finally {
            commit(MutationTypes.SET_COMPANIES_DATA_IS_LOADING, false)
        }
    },
    [ActionTypes.SET_COMPANIES_LIST_PAGE]({commit}, page: number) {
        commit(MutationTypes.SET_COMPANIES_LIST_PAGE, page)
    },
    [ActionTypes.SET_COMPANIES_LIST_PER_PAGE]({commit}, perPage: number) {
        commit(MutationTypes.SET_COMPANIES_LIST_PER_PAGE, perPage)
    },
    [ActionTypes.SET_COMPANIES_LIST_SORT]({commit}, sort: CompaniesListSortModel) {
        commit(MutationTypes.SET_COMPANIES_SORT_VALUE, sort)
    },
    [ActionTypes.SET_COMPANIES_LIST_FILTER]({commit}, filter: CompaniesListFilterModel) {
        commit(MutationTypes.SET_COMPANIES_FILTER_VALUE, filter)
        commit(MutationTypes.SET_COMPANIES_LIST_PAGE, 1)
    },
    async [ActionTypes.SET_COMPANIES_COMPANY_CHANGE_STATUS]({commit, dispatch, getters}, model: ChangeCompanyStatusModel) {

        const client: BaseClient = getClientInstance()

        commit(MutationTypes.SET_COMPANIES_DATA_IS_LOADING, true)

        try{
            await client.changeCompanyStatus(model.id, {
                status: model.status == 'ACTIVE' ? 'INACTIVE' : 'ACTIVE'
            })
        }
        catch(error){
            console.log('changeCompanyStatus', error)
        }
        finally {
            dispatch(ActionTypes.SET_COMPANIES_LIST)
            if (getters.getCompaniesSelectedCompany && getters.getCompaniesSelectedCompany.id && getters.getCompaniesSelectedCompany.id == model.id) {
                dispatch(ActionTypes.SET_COMPANIES_SELECTED_COMPANY, model.id)
            }            
        }
        
    },
    async [ActionTypes.SET_COMPANIES_COMPANY_DELETE]({commit, dispatch}, model: DeleteCompaniesListRequestModel) {

        const client: BaseClient = getClientInstance()

        commit(MutationTypes.SET_COMPANIES_DATA_IS_LOADING, true)

        try{
            await client.deleteCompaniesList(model)
        }
        catch(error){
            console.log('deleteCompaniesList', error)
        }
        finally {
            dispatch(ActionTypes.SET_COMPANIES_LIST)
        }
    },
    async [ActionTypes.SET_COMPANIES_COMPANY_CREATE]({commit}, name: string) {

        const client: BaseClient = getClientInstance()

        commit(MutationTypes.SET_COMPANIES_DATA_IS_LOADING, true)

        try{
            const company: CompanyModel = await client.createCompany({name})
            commit(MutationTypes.SET_COMPANIES_SELECTED_COMPANY, company)
        }
        catch(error){
            console.log('createCompany', error)
        }
        finally {
            commit(MutationTypes.SET_COMPANIES_DATA_IS_LOADING, false)
        }
    },
    async [ActionTypes.SET_COMPANIES_COMPANY_CHANGE_NAME]({commit, dispatch, getters}, name: string) {

        const client: BaseClient = getClientInstance()

        commit(MutationTypes.SET_COMPANIES_DATA_IS_LOADING, true)

        const id = getters.getCompaniesSelectedCompany.id

        try{
            await client.changeCompanyName(id, {name})
        }
        catch(error){
            console.log('changeCompanyName', error)
        }
        finally {
            dispatch(ActionTypes.SET_COMPANIES_SELECTED_COMPANY, id)
            dispatch(ActionTypes.SET_COMPANIES_LIST)
        }
    },
    async [ActionTypes.SET_COMPANIES_SELECTED_COMPANY]({commit}, id: number) {

        const client: BaseClient = getClientInstance()

        commit(MutationTypes.SET_COMPANIES_DATA_IS_LOADING, true)

        try{
            const response: GetCompanyResponseModel = await client.getCompany(id)
            commit(MutationTypes.SET_COMPANIES_SELECTED_COMPANY, response)
        }
        catch(error){
            console.log('getCompany', error)
        }
        finally {
            commit(MutationTypes.SET_COMPANIES_DATA_IS_LOADING, false)
        }
    },
    async [ActionTypes.SET_COMPANIES_SELECTED_COMPANY_WITH_USERS_LIST]({commit, dispatch}, id: number) {

        const client: BaseClient = getClientInstance()

        commit(MutationTypes.SET_COMPANIES_DATA_IS_LOADING, true)

        try{
            const response: GetCompanyWithUsersResponseModel = await client.getCompanyWithUsers(id)
            const employeeList: CompanyEmployeeModel [] = response.users
            const company: CompanyModel = {
                id: response.id,
                name: response.name,
                status: response.status,
                createdAt: response.createdAt,
                updatedAt: response.updatedAt
            }
            commit(MutationTypes.SET_COMPANIES_SELECTED_COMPANY, company)
            dispatch(StoreActionTypes.SET_EMPLOYEES_LIST_FROM_ARRAY, employeeList)
        }
        catch(error){
            console.log('getCompanyWithUsers', error)
        }
        finally {
            commit(MutationTypes.SET_COMPANIES_DATA_IS_LOADING, false)
        }
    },
    [ActionTypes.SET_COMPANIES_SELECTED_COMPANY_CLEAR]({commit, dispatch}) {
        commit(MutationTypes.SET_COMPANIES_SELECTED_COMPANY, null)
        dispatch(StoreActionTypes.SET_EMPLOYEES_LIST_FROM_ARRAY, [])
    }
}